'use client';

/**
 * Poster-style portrait tile (3:4). Image fills the card; title +
 * meta overlay the bottom on a dark gradient. Denser than the default
 * variant — fits more cards per row, optimised for visual browsing.
 */
import { useTranslation } from 'react-i18next';
import { PortalLink } from '@akademi/site-builder-runtime';
import { Card } from '@akademi/ui';
import { CourseProgressBar } from '../_shared/CourseProgressBar';
import { CourseCoverImage } from '../_shared/CourseCoverImage';
import { cardReset } from '../_shared/styles';
import type { CourseCardProps } from './CourseCard.types';
import type { CardGridSpec } from './gridSpecs';
import type { ProgressTextPlace } from './registry';

export function CompactCourseCard({
  course: c,
  progressBar: {
    show: showProgress = false,
    showText: showProgressText = false,
    textPlace: progressTextPlace = 'outside-right',
  } = {},
}: {
  course: CourseCardProps;
  progressBar?: { show?: boolean; showText?: boolean; textPlace?: ProgressTextPlace };
}) {
  const { t } = useTranslation('common');
  return (
    <Card asChild>
      <PortalLink
        href={c.href}
        style={{
          ...cardReset,
          position: 'relative',
          display: 'block',
          aspectRatio: 'var(--app-course-image-aspect-ratio, 3 / 4)',
          borderRadius: 'var(--radius-md, 10px)',
          overflow: 'hidden',
          color: 'var(--color-text-on-brand, #fff)',
          textDecoration: 'none',
          transition: 'transform 200ms ease',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'scale(1.03)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = '';
        }}
      >
        <CourseCoverImage
          image={typeof c.image === 'string' ? c.image : undefined}
          resizeTo="480x0"
          sizes="(max-width: 640px) 50vw, 260px"
        />
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background:
              'linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.4) 35%, rgba(0,0,0,0) 60%)',
          }}
        />
        {c.duration && (
          <div
            style={{
              position: 'absolute',
              top: 8,
              right: 8,
              padding: 'var(--space-1, 4px) var(--space-2, 8px)',
              background: 'rgba(0,0,0,0.7)',
              backdropFilter: 'blur(4px)',
              borderRadius: 4,
              fontSize: '11px',
              fontWeight: 600,
              color: '#fff',
            }}
          >
            {c.duration}
          </div>
        )}
        <div
          style={{
            position: 'absolute',
            left: 12,
            right: 12,
            bottom: 10,
          }}
        >
          <h3
            style={{
              margin: 0,
              fontSize: 'var(--font-size-sm, 14px)',
              fontWeight: 700,
              lineHeight: 1.25,
              color: 'var(--color-text-primary-inverse, #e4e4e7)',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              textShadow: '0 1px 4px rgba(0,0,0,0.5)',
            }}
          >
            {c.title}
          </h3>
          {typeof c.enrolled === 'number' && (
            <div
              style={{
                marginTop: 4,
                fontSize: '11px',
                color: 'var(--color-text-primary-inverse, #e4e4e7)',
                fontWeight: 500,
              }}
            >
              {c.enrolled.toLocaleString()} {t('label.participant')}
            </div>
          )}
          {showProgress && typeof c.progress === 'number' && (
            <div style={{ padding: '5px 0px' }}>
              <CourseProgressBar
                progress={c.progress}
                showText={showProgressText}
                textPlace={progressTextPlace}
              />
            </div>
          )}
        </div>
      </PortalLink>
    </Card>
  );
}

CompactCourseCard.gridSpec = {
  kind: 'grid',
  minCardWidth: 160,
  gap: 'clamp(10px, 1.5vw, 14px)',
} satisfies CardGridSpec;
